import LicenseBuilderSortable from "../licenseBuilderSortable";
import License from "../license";
import * as _ from 'lodash';
import Guid from "~/helpers/guid";
import {DurationUnit} from "../../../../common/enums/durationUnit";
import {Month} from "../../../../common/enums/month";
import {DurationType} from "../../../../common/enums/durationType";
import IRepo from "../../../../common/interfaces/repo";
import Repo from "../../../../common/state/modules/repo";
import Recipient from "./recipient";
import {RelationType} from "../../../../common/models/relationType";
import {ObjectHelper} from "../../../../common/helpers/objectHelper";
import {InstanceHelper} from "../../../../common/helpers/instanceHelper";
import {NotificationDeliveryMethod} from "../../../form_builder/enums/reminderDeliveryMethod";
import {
  LicenseNotificationRelativeToLabel,
  LicenseNotificationRelativeToType
} from "./licenseNotificationRelativeToType";
import {INotification} from "../../../../common/interfaces/notificationInterfaces";

export default class LicenseNotification extends LicenseBuilderSortable implements INotification {
  client_id: string = Guid.create();
  subject: string = '';
  body: string = '';
  delivery_methods: [NotificationDeliveryMethod.ViaInSystem];
  is_after: boolean;
  relative_to: LicenseNotificationRelativeToType;
  type: number;
  day: number;
  month: Month;
  amount: number;
  unit: DurationUnit;
  license_notification_recipients: { [key: string]: Recipient } = {};

  except = ['license_notification_recipients'];

  static repo: IRepo<LicenseNotification> = new Repo<LicenseNotification>('licenseNotifications', LicenseNotification);

  constructor(license: License) {
    super();
    this.defineProperty('client_id', Guid.create());
    this.defineProperty('subject', '');
    this.defineProperty('body', '');
    this.defineProperty('delivery_methods', [NotificationDeliveryMethod.ViaInSystem]);
    this.defineProperty('is_after', false);
    this.defineProperty('relative_to', LicenseNotificationRelativeToType.validityEnd);
    this.defineProperty('type', DurationType.fixedDate);
    this.defineProperty('day', 31);
    this.defineProperty('month', Month.december);
    this.defineProperty('amount', 0);
    this.defineProperty('unit', DurationUnit.hour);
    this.defineProperty('sort_order', license.nextSortOrderFor(this.rootName));
    this.rootModel = license;
    this.license = license;
  }

  get EMBEDDED_MODELS(): { [key: string]: { type: any, relationType: RelationType } } {
    return {
      'license_notification_recipients': { type: Recipient, relationType: RelationType.dictionary }
    };
  }

  createEmbeddedObject = {
    license_notification_recipients: (parent, json): Recipient => { return new Recipient(parent);}
  };

  get isCollectionRoot(): boolean {
    return true;
  }

  get childRootName(): string {
    return 'license_notification_recipients';
  }

  get recipientsDisplayNames(): string[] {
    return (<any>Object).values(this.license_notification_recipients).map(r => r.display);
  }

  get displayTriggerString(): string {
    let notificationLabel = '';
    if (this.type === DurationType.timespan) {
      notificationLabel = this.amount.toString(10) + ' ';
      notificationLabel += DurationUnit[this.unit] + (this.amount === 1 ? ' ' : 's ');
      notificationLabel += this.is_after ? 'after ' : 'before ';
      notificationLabel += LicenseNotificationRelativeToLabel[this.relative_to].toLowerCase();
    } else {
      notificationLabel = 'on ' + _.capitalize(Month[this.month]) + ' ';
      notificationLabel += this.day.toString(10) + ' ';
    }

    return notificationLabel;
  }

  get recipientsArray(): Recipient[] {
    return ObjectHelper.toArray(this.license_notification_recipients);
  }

  get isValid(): boolean {
    return this.validate( 'license_notification_recipients', 'subject', 'body', 'delivery_methods');
  }

  validate(...props): boolean {
    let valid = true;
    for (let i = 0; i < props.length; i++) {
      const prop = props[i];
      switch (prop) {
        case 'license_notification_recipients':
        case 'delivery_methods':
          valid = InstanceHelper.isPresent(this[prop]);
          break;
        case 'subject':
        case 'body':
          valid = this[prop].trim().length > 0;
          break;
        default:
          break;
      }
      if (!valid) {
        break;
      }
    }
    return valid;
  }

  get recipientsToString(): string {
    const length = this.recipientsDisplayNames.length;
    switch (length) {
      case 0:
        return '';
      case 1:
        return this.recipientsDisplayNames[0];
      case 2:
        return `${this.recipientsDisplayNames[0]} and ${this.recipientsDisplayNames[1]}`;
      default:
        let result = '';
        for (let i = 0; i < length - 1; i++) {
          result += `${this.recipientsDisplayNames[i]}, `;
        }
        result += `and ${this.recipientsDisplayNames[length - 1]}`;
        return result;
    }
  }

  get rootName(): string {
    return 'license_notifications';
  }

  get displayName(): string {
    return 'Notifications';
  }
}